import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import cloudApi from "../../../services/cloudApi";
import { processData } from "../utils/dataProcessing";

export const useCloudTableData = (filters, tableId = null, searchTerm = "") => {
  // State for managing table data
  const [tableHeader, setTableHeader] = useState(null);
  const [columns, setColumns] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPaginating, setIsPaginating] = useState(false);
  const [error, setError] = useState(null);
  const [errorStatusCode, setErrorStatusCode] = useState(null);
  const [noTableSelected, setNoTableSelected] = useState(!tableId);

  // Track if we have data to distinguish initial load from pagination
  const hasDataRef = useRef(false);

  // Debounced search term for API calls
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Track if search is in progress (debouncing)
  const isSearching = searchTerm !== debouncedSearch;

  // Debounce search input (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Track previous search to reset pagination when search changes
  const prevSearchRef = useRef(debouncedSearch);

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  });

  // Reset to page 1 when search changes
  useEffect(() => {
    if (prevSearchRef.current !== debouncedSearch) {
      prevSearchRef.current = debouncedSearch;
      if (pagination.page !== 1) {
        setPagination((prev) => ({ ...prev, page: 1 }));
      }
    }
  }, [debouncedSearch, pagination.page]);

  // Load data for the table
  useEffect(() => {
    if (!tableId) {
      setLoading(false);
      setNoTableSelected(true);
      return;
    }

    setNoTableSelected(false);

    // Reset hasDataRef and pagination when table changes to show full loading for new table
    if (tableId !== hasDataRef.tableId) {
      hasDataRef.current = false;
      hasDataRef.tableId = tableId;

      // Reset state for new table to show loading indicator
      setData([]);
      setColumns([]);
      setTableHeader(null);
      setLoading(true);

      // Reset pagination to page 1 for new table
      if (pagination.page !== 1) {
        setPagination(prev => ({ ...prev, page: 1 }));
        return; // Exit early, this effect will re-run with page 1
      }
    }

    const loadData = async () => {
      // Use isPaginating for subsequent loads, loading for initial load
      if (hasDataRef.current) {
        setIsPaginating(true);
      } else {
        setLoading(true);
      }
      setError(null);
      setErrorStatusCode(null);

      try {
        const response = await cloudApi.getTableWithRows(tableId, {
          page: pagination.page,
          limit: pagination.limit,
          sortBy: "sequence_number",
          sortOrder: "asc",
          search: debouncedSearch,
        });

        if (!response) {
          setError(`Table with ID ${tableId} not found`);
          setLoading(false);
          setIsPaginating(false);
          return;
        }

        // Set table header (the full response is the table metadata)
        setTableHeader(response);
        const tableColumns = response.columns || [];
        setColumns(tableColumns);

        // Rows are already in the correct format from the API
        const rows = (response.rows || []).map((row) => ({
          id: row.id,
          data: row.data,
          metadata: row.metadata,
          sequence_number: row.sequence_number,
          extracted_at: row.extracted_at,
          created_at: row.created_at,
        }));

        setData(rows);
        hasDataRef.current = true;

        // Update pagination
        setPagination(response.pagination || pagination);

        setLoading(false);
        setIsPaginating(false);
      } catch (err) {
        console.error("[useCloudTableData] Error loading data:", err);
        setError(err.message || "Failed to load data");
        setErrorStatusCode(err.statusCode || null);
        setLoading(false);
        setIsPaginating(false);
      }
    };

    loadData();
  }, [tableId, pagination.page, pagination.limit, debouncedSearch]);

  // Navigate to a specific page
  const goToPage = useCallback((page) => {
    setPagination((prev) => ({ ...prev, page }));
  }, []);

  // Optimistically drop rows from the current page after a delete — the server
  // total is kept in step locally and re-syncs on the next fetch.
  const removeRowsLocal = useCallback((rowIds) => {
    const ids = new Set(rowIds);
    setData((prev) => prev.filter((row) => !ids.has(row.id)));
    setPagination((prev) => ({ ...prev, total: Math.max(0, prev.total - ids.size) }));
  }, []);

  // Refresh current data
  const refreshData = useCallback(async () => {
    if (!tableId) return;

    try {
      const response = await cloudApi.getTableWithRows(tableId, {
        page: pagination.page,
        limit: pagination.limit,
        sortBy: "sequence_number",
        sortOrder: "asc",
        search: debouncedSearch,
      });

      if (!response) return;

      // Update table header and columns
      setTableHeader(response);
      const tableColumns = response.columns || [];
      setColumns(tableColumns);

      // Transform rows
      const rows = (response.rows || []).map((row) => ({
        id: row.id,
        data: row.data,
        metadata: row.metadata,
        sequence_number: row.sequence_number,
        extracted_at: row.extracted_at,
        created_at: row.created_at,
      }));

      setData(rows);

      // Update pagination
      setPagination(response.pagination || pagination);
    } catch (err) {
      console.error("[useCloudTableData] Error refreshing data:", err);
      // Don't set error state to avoid disrupting the UI during auto-refresh
    }
  }, [tableId, pagination.page, pagination.limit, debouncedSearch]);

  // Process columns and data to handle filtering
  const processedData = useMemo(() => {
    return processData(data, columns, filters, false);
  }, [data, columns, filters]);

  return {
    tableId,
    tableHeader,
    columns,
    setColumns, // Expose for column editing
    data,
    loading,
    isPaginating,
    error,
    errorStatusCode,
    processedData,
    pagination,
    refreshData,
    goToPage,
    removeRowsLocal, // Expose for row deletion
    noTableSelected,
    isSearching, // True when search is debouncing
  };
};
